export interface TypesenseSearchParams {
  collection: string
  q: string
  query_by: string
  page?: number
  per_page?: number
  filter_by?: string
  facet_by?: string
  max_facet_values?: number
  include_fields?: string
  /** Fields left out of each returned document (e.g. the embedding vector). */
  exclude_fields?: string
  sort_by?: string
  /** Named saved search params created via scripts/create-typesense-sort-presets.mjs. Mutually exclusive with sort_by — an explicit sort_by on the same request replaces the preset's rather than merging with it. */
  preset?: string
  /** e.g. `embedding:([], id: 123, k: 8)` — nearest neighbors of an already-indexed document's vector. */
  vector_query?: string
  /** Typo tolerance: max typos (0–2), or a per-`query_by`-field list like `2,2,0`. */
  num_typos?: number | string
  min_len_1typo?: number
  min_len_2typo?: number
  /** How a multi-field text score is formed; `max_weight` ranks by the highest-priority matching field. */
  text_match_type?: 'max_score' | 'max_weight' | 'sum_score'
  /** Rank documents higher when the query words appear earlier in the text. */
  prioritize_token_position?: boolean
  /** Cluster stopword set id; its words are dropped from the keyword side of `q`. */
  stopwords?: string
}

export interface TypesenseHit<T> {
  document: T
  highlight?: Record<string, unknown>
  text_match?: number
  vector_distance?: number
}

export interface TypesenseSearchResult<T> {
  found: number
  page: number
  search_time_ms?: number
  hits?: TypesenseHit<T>[]
  /** Set by a curation rule's `metadata` when that rule fires. */
  metadata?: Record<string, unknown>
  facet_counts?: Array<{
    field_name: string
    counts: Array<{ value: string; count: number }>
  }>
}

interface TypesenseClientOptions {
  host: string
  apiKey: string
  /**
   * Serve the request from the cluster's search cache (`use_cache`). On
   * multi_search it's a URL param, so it covers every search in the request.
   * The TTL is the cluster default (60s); `cache_ttl` can only be set on a
   * scoped API key. The cache key is the exact request, so leave it off for
   * requests carrying per-viewer params (geo sorts).
   */
  useCache?: boolean
}

/**
 * Raw REST call to Typesense's /multi_search — not the typesense-js SDK,
 * to match the repo's $fetch-based data-fetching conventions and keep one
 * request shape for both single-index and future multi-index callers.
 */
export const typesenseMultiSearch = async <T>(
  searches: TypesenseSearchParams[],
  { host, apiKey, useCache }: TypesenseClientOptions
): Promise<TypesenseSearchResult<T>[]> => {
  const response = await $fetch<{
    results: (TypesenseSearchResult<T> & { error?: string; code?: number })[]
  }>(`${host}/multi_search`, {
    method: 'POST',
    headers: { 'X-TYPESENSE-API-KEY': apiKey },
    ...(useCache ? { query: { use_cache: true } } : {}),
    body: { searches },
  })

  // multi_search answers HTTP 200 even when a search fails, carrying the
  // error inside that search's result. Left unchecked it reads as zero hits.
  response.results.forEach((result, i) => {
    if (result.error) {
      throw new Error(
        `Typesense search on ${searches[i]?.collection} failed (${result.code}): ${result.error}`
      )
    }
  })

  return response.results
}

export const typesenseSearch = async <T>(
  params: TypesenseSearchParams,
  options: TypesenseClientOptions
): Promise<TypesenseSearchResult<T>> => {
  const results = await typesenseMultiSearch<T>([params], options)
  return results[0]!
}
